// src/components/Common/BusinessHoursSchedule.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Schedule as ScheduleIcon,
  Restaurant as LunchIcon,
  DinnerDining as DinnerIcon,
  Coffee as BreakIcon,
  MoreHoriz as CustomIcon,
} from '@mui/icons-material';

interface DaySchedule {
  day: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  breaks: BreakPeriod[];
}

interface BreakPeriod {
  id?: string;
  type: 'lunch' | 'supper' | 'break' | 'custom';
  name: string;
  startTime: string;
  endTime: string;
  action: number;
  target?: string;
}

interface BusinessHoursScheduleProps {
  schedule: DaySchedule[];
  outsideHoursAction: number;
  outsideHoursTarget?: string;
  onScheduleChange: (schedule: DaySchedule[]) => void;
  onOutsideHoursChange: (action: number, target?: string) => void;
  overflowActions: any[];
  overflowTargets: any[];
  onFetchTargets: (actionId: number) => void;
  onSave?: () => void;
}

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday', short: 'Mon' },
  { value: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { value: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { value: 'thursday', label: 'Thursday', short: 'Thu' },
  { value: 'friday', label: 'Friday', short: 'Fri' },
  { value: 'saturday', label: 'Saturday', short: 'Sat' },
  { value: 'sunday', label: 'Sunday', short: 'Sun' },
];

const BREAK_TYPES = [
  { value: 'lunch', label: 'Lunch', icon: <LunchIcon /> },
  { value: 'supper', label: 'Supper', icon: <DinnerIcon /> },
  { value: 'break', label: 'Break', icon: <BreakIcon /> },
  { value: 'custom', label: 'Custom', icon: <CustomIcon /> },
];

const BusinessHoursSchedule: React.FC<BusinessHoursScheduleProps> = ({
  schedule,
  outsideHoursAction,
  outsideHoursTarget,
  onScheduleChange,
  onOutsideHoursChange,
  overflowActions,
  overflowTargets,
  onFetchTargets,
  onSave,
}) => {
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState<BreakPeriod | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>('');
  
  const [breakForm, setBreakForm] = useState<Partial<BreakPeriod>>({
    type: 'lunch',
    name: 'Lunch Break',
    startTime: '12:00',
    endTime: '13:00',
    action: 7, // Voicemail
  });

  // Fetch targets on mount if the default action requires them
  useEffect(() => {
    const requiresTargetActions = [1, 3, 4, 7, 11, 16, 17, 19, 20];
    if (requiresTargetActions.includes(outsideHoursAction)) {
      onFetchTargets(outsideHoursAction);
    }
  }, [outsideHoursAction, onFetchTargets]); // Run when action changes

  const getBreakIcon = (type: string) => {
    return BREAK_TYPES.find(t => t.value === type)?.icon || <CustomIcon />;
  };

  const handleDayToggle = (day: string, isOpen: boolean) => {
    const newSchedule = schedule.map(d => 
      d.day === day ? { ...d, isOpen } : d
    );
    onScheduleChange(newSchedule);
  };

  const handleTimeChange = (day: string, field: 'openTime' | 'closeTime', value: string) => {
    const newSchedule = schedule.map(d => 
      d.day === day ? { ...d, [field]: value } : d
    );
    onScheduleChange(newSchedule);
  };

  const handleAddBreak = (day: string) => {
    setSelectedDay(day);
    setEditingBreak(null);
    setBreakForm({
      type: 'lunch',
      name: 'Lunch Break',
      startTime: '12:00',
      endTime: '13:00',
      action: 7,
    });
    // Fetch targets for default action (voicemail)
    onFetchTargets(7);
    setBreakDialogOpen(true);
  };

  const handleEditBreak = (day: string, breakPeriod: BreakPeriod) => {
    setSelectedDay(day);
    setEditingBreak(breakPeriod);
    setBreakForm(breakPeriod);
    if (breakPeriod.action) {
      onFetchTargets(breakPeriod.action);
    }
    setBreakDialogOpen(true);
  };

  const handleSaveBreak = () => {
    const newSchedule = schedule.map(d => {
      if (d.day === selectedDay) {
        const breaks = [...d.breaks];
        if (editingBreak) {
          const index = breaks.findIndex(b => b.id === editingBreak.id);
          if (index >= 0) {
            breaks[index] = { ...breakForm, id: editingBreak.id } as BreakPeriod;
          }
        } else {
          breaks.push({ ...breakForm, id: Date.now().toString() } as BreakPeriod);
        }
        return { ...d, breaks };
      }
      return d;
    });
    onScheduleChange(newSchedule);
    setBreakDialogOpen(false);
  };

  const handleDeleteBreak = (day: string, breakId: string) => {
    const newSchedule = schedule.map(d => {
      if (d.day === day) {
        return { ...d, breaks: d.breaks.filter(b => b.id !== breakId) };
      }
      return d;
    });
    onScheduleChange(newSchedule);
  };

  const copyScheduleToWeekdays = (fromDay: string) => {
    const sourceDaySchedule = schedule.find(d => d.day === fromDay);
    if (!sourceDaySchedule) return;

    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const newSchedule = schedule.map(d => {
      if (weekdays.includes(d.day) && d.day !== fromDay) {
        return {
          ...d,
          isOpen: sourceDaySchedule.isOpen,
          openTime: sourceDaySchedule.openTime,
          closeTime: sourceDaySchedule.closeTime,
          breaks: sourceDaySchedule.breaks.map(b => ({ ...b, id: `${d.day}-${b.id}` })),
        };
      }
      return d;
    });
    onScheduleChange(newSchedule);
  };

  const renderTimelineVisualization = (daySchedule: DaySchedule) => {
    if (!daySchedule.isOpen) return null;

    const hourWidth = 35; // Reduced from 40 to fit better
    const startHour = 0;
    const endHour = 24;
    const totalWidth = (endHour - startHour) * hourWidth;

    const timeToPosition = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      return ((hours + minutes / 60) - startHour) * hourWidth;
    };

    const openPos = timeToPosition(daySchedule.openTime);
    const closePos = timeToPosition(daySchedule.closeTime);

    return (
      <Box sx={{ 
        overflowX: 'auto', 
        overflowY: 'hidden',
        mt: 2, 
        mb: 2,
        position: 'relative',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#555',
          },
        },
      }}>
        <Box sx={{ position: 'relative', height: 60, minWidth: totalWidth, pt: 2 }}>
          {/* Hour labels row */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: totalWidth, height: 20 }}>
            {Array.from({ length: endHour - startHour + 1 }, (_, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: i * hourWidth - 10,
                  top: 0,
                  width: 20,
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#666',
                  fontWeight: i % 6 === 0 ? 'bold' : 'normal',
                }}
              >
                {(startHour + i).toString().padStart(2, '0')}
              </Box>
            ))}
          </Box>

          {/* Timeline background with hour divisions */}
          <Box sx={{ position: 'absolute', top: 20, left: 0, width: totalWidth, height: 40 }}>
            {Array.from({ length: endHour - startHour }, (_, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: i * hourWidth,
                  top: 0,
                  width: hourWidth,
                  height: '100%',
                  borderLeft: i === 0 ? 'none' : '1px solid #e0e0e0',
                  backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
                }}
              />
            ))}
          </Box>

          {/* Open hours bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 35,
              left: openPos,
              width: closePos - openPos,
              height: 12,
              bgcolor: 'primary.main',
              borderRadius: 1,
              boxShadow: 1,
            }}
          />

          {/* Break periods */}
          {daySchedule.breaks.map((breakPeriod) => {
            const breakStart = timeToPosition(breakPeriod.startTime);
            const breakEnd = timeToPosition(breakPeriod.endTime);
            return (
              <Box
                key={breakPeriod.id}
                sx={{
                  position: 'absolute',
                  top: 33,
                  left: breakStart,
                  width: breakEnd - breakStart,
                  height: 16,
                  bgcolor: 'error.main',
                  borderRadius: 1,
                  border: '2px solid white',
                  boxShadow: 1,
                  zIndex: 1,
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon /> Business Hours Schedule
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Scroll horizontally on the timeline to view all 24 hours
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {DAYS_OF_WEEK.map((dayInfo) => {
            const daySchedule = schedule.find(s => s.day === dayInfo.value) || {
              day: dayInfo.value,
              isOpen: false,
              openTime: '09:00',
              closeTime: '17:00',
              breaks: [],
            };

            return (
              <Grid item xs={12} key={dayInfo.value}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle1" sx={{ minWidth: 100 }}>
                          {dayInfo.label}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={daySchedule.isOpen}
                              onChange={(e) => handleDayToggle(dayInfo.value, e.target.checked)}
                              size="small"
                            />
                          }
                          label={daySchedule.isOpen ? 'Open' : 'Closed'}
                        />
                      </Box>
                      {daySchedule.isOpen && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => copyScheduleToWeekdays(dayInfo.value)}
                            title="Copy to weekdays"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {daySchedule.isOpen && (
                      <>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2, 
                          alignItems: 'center', 
                          mb: 2,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' }
                        }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: { xs: '100%', sm: 'auto' } }}>
                            <TextField
                              label="Open"
                              type="time"
                              size="small"
                              value={daySchedule.openTime}
                              onChange={(e) => handleTimeChange(dayInfo.value, 'openTime', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: { xs: '45%', sm: 'auto' } }}
                            />
                            <Typography>to</Typography>
                            <TextField
                              label="Close"
                              type="time"
                              size="small"
                              value={daySchedule.closeTime}
                              onChange={(e) => handleTimeChange(dayInfo.value, 'closeTime', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: { xs: '45%', sm: 'auto' } }}
                            />
                          </Box>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddBreak(dayInfo.value)}
                            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                          >
                            Add Break
                          </Button>
                        </Box>

                        {/* Visual Timeline */}
                        {renderTimelineVisualization(daySchedule)}

                        {/* Breaks List */}
                        {daySchedule.breaks.length > 0 && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Breaks:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {daySchedule.breaks.map((breakPeriod) => (
                                <Chip
                                  key={breakPeriod.id}
                                  icon={getBreakIcon(breakPeriod.type)}
                                  label={`${breakPeriod.name}: ${breakPeriod.startTime}-${breakPeriod.endTime}`}
                                  onDelete={() => handleDeleteBreak(dayInfo.value, breakPeriod.id!)}
                                  onClick={() => handleEditBreak(dayInfo.value, breakPeriod)}
                                  color="default"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Outside Business Hours
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={outsideHoursAction}
                onChange={(e) => {
                  const actionId = Number(e.target.value);
                  onOutsideHoursChange(actionId);
                  onFetchTargets(actionId);
                }}
                label="Action"
              >
                {overflowActions.map((action) => (
                  <MenuItem key={action.action_key} value={action.action_key}>
                    {action.action_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {overflowTargets.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Target</InputLabel>
                <Select
                  value={outsideHoursTarget || ''}
                  onChange={(e) => onOutsideHoursChange(outsideHoursAction, e.target.value)}
                  label="Target"
                >
                  {overflowTargets.map((target) => (
                    <MenuItem key={target.value} value={target.value}>
                      {target.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              if (onSave) {
                onSave();
              }
            }}
          >
            Save Business Hours
          </Button>
        </Box>
      </Paper>

      {/* Break Dialog */}
      <Dialog open={breakDialogOpen} onClose={() => setBreakDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBreak ? 'Edit Break' : 'Add Break'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Break Type</InputLabel>
              <Select
                value={breakForm.type || 'lunch'}
                onChange={(e) => {
                  const type = e.target.value as any;
                  const defaultName = BREAK_TYPES.find(t => t.value === type)?.label || 'Custom Break';
                  setBreakForm({ ...breakForm, type, name: defaultName });
                }}
                label="Break Type"
              >
                {BREAK_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {breakForm.type === 'custom' && (
              <TextField
                fullWidth
                size="small"
                label="Break Name"
                value={breakForm.name || ''}
                onChange={(e) => setBreakForm({ ...breakForm, name: e.target.value })}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                size="small"
                fullWidth
                value={breakForm.startTime || '12:00'}
                onChange={(e) => setBreakForm({ ...breakForm, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                fullWidth
                value={breakForm.endTime || '13:00'}
                onChange={(e) => setBreakForm({ ...breakForm, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Action During Break</InputLabel>
              <Select
                value={breakForm.action || 7}
                onChange={(e) => {
                  const actionId = Number(e.target.value);
                  setBreakForm({ ...breakForm, action: actionId, target: '' });
                  onFetchTargets(actionId);
                }}
                label="Action During Break"
              >
                {overflowActions.map((action) => (
                  <MenuItem key={action.action_key} value={action.action_key}>
                    {action.action_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {overflowTargets.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel>Target</InputLabel>
                <Select
                  value={breakForm.target || ''}
                  onChange={(e) => setBreakForm({ ...breakForm, target: e.target.value })}
                  label="Target"
                >
                  {overflowTargets.map((target) => (
                    <MenuItem key={target.value} value={target.value}>
                      {target.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreakDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveBreak}
            variant="contained"
            disabled={!breakForm.startTime || !breakForm.endTime}
          >
            {editingBreak ? 'Update' : 'Add'} Break
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessHoursSchedule;